import type { AnimationSource, PhaseTiming } from '../characters';
import { DEFAULT_ATTACK_PHASES } from './constants';

export type AnimationOverrides = Partial<Omit<AnimationSource, 'file'>>;

export interface AnimationSnippetDescriptor {
  description: string;
  defaults: AnimationOverrides;
  tags: readonly string[];
}

const ANIMATION_SNIPPETS = {
  'idle.basic': {
    description: 'Looping idle base for Mixamo rigs',
    defaults: {},
    tags: ['movement', 'loop']
  },
  'run.basic': {
    description: 'Forward run cycle baseline',
    defaults: {},
    tags: ['movement', 'loop']
  },
  'jump.default': {
    description: 'Default jump cycle with neutral speed',
    defaults: { naturalSpeed: null },
    tags: ['movement', 'air']
  },
  'attack.default': {
    description: 'Standard melee attack phases',
    defaults: {
      phases: DEFAULT_ATTACK_PHASES,
      exitTime: { enabled: true, time: 0.8 },
      speed: 3
    },
    tags: ['combat', 'phased']
  }
} satisfies Record<string, AnimationSnippetDescriptor>;

export type AnimationSnippetId = keyof typeof ANIMATION_SNIPPETS;

export interface AnimationSnippetDefinition {
  id: AnimationSnippetId;
  description: string;
  tags: string[];
  defaults: AnimationOverrides;
}

const clonePhaseTiming = (phases?: PhaseTiming): PhaseTiming | undefined => {
  if (!phases) {
    return undefined;
  }
  return {
    windup: phases.windup ? { ...phases.windup } : undefined,
    active: phases.active ? { ...phases.active } : undefined,
    release: phases.release ? { ...phases.release } : undefined,
    recovery: phases.recovery ? { ...phases.recovery } : undefined
  };
};

const cloneExitTime = (source?: AnimationSource['exitTime']) => {
  if (!source) {
    return undefined;
  }
  return { ...source };
};

const cloneOverrides = (overrides?: AnimationOverrides): AnimationOverrides | undefined => {
  if (!overrides) {
    return overrides;
  }
  const clone: AnimationOverrides = { ...overrides };
  if ('phases' in overrides) {
    clone.phases = clonePhaseTiming(overrides.phases);
  }
  if ('exitTime' in overrides) {
    clone.exitTime = cloneExitTime(overrides.exitTime);
  }
  if ('mask' in overrides) {
    const mask = overrides.mask;
    clone.mask = Array.isArray(mask) ? [...mask] : mask;
  }
  if ('blend' in overrides) {
    const blend = overrides.blend;
    clone.blend = blend ? { ...blend } : blend;
  }
  return clone;
};

const applyOverrides = (
  defaults?: AnimationOverrides,
  overrides?: AnimationOverrides
): AnimationOverrides => {
  const result = cloneOverrides(defaults) ?? {};
  if (!overrides) {
    return result;
  }
  const incoming = cloneOverrides(overrides);
  if (!incoming) {
    return result;
  }
  for (const key of Object.keys(incoming) as (keyof AnimationOverrides)[]) {
    (result as Record<string, unknown>)[key as string] = incoming[key];
  }
  return result;
};

const buildAnimation = (
  file: string,
  descriptor: AnimationSnippetDescriptor,
  overrides?: AnimationOverrides
): AnimationSource => {
  const merged = applyOverrides(descriptor.defaults, overrides);
  const finalOverrides = cloneOverrides(merged) ?? {};
  return {
    file,
    ...finalOverrides
  };
};

export const listAnimationSnippets = (): AnimationSnippetDefinition[] =>
  (Object.entries(ANIMATION_SNIPPETS) as [AnimationSnippetId, AnimationSnippetDescriptor][]).map(
    ([id, descriptor]) => ({
      id,
      description: descriptor.description,
      tags: [...descriptor.tags],
      defaults: cloneOverrides(descriptor.defaults) ?? {}
    })
  );

export const useAnimationSnippet = (
  id: AnimationSnippetId,
  file: string,
  overrides?: AnimationOverrides
): AnimationSource => {
  const descriptor = ANIMATION_SNIPPETS[id];
  if (!descriptor) {
    throw new Error(`Unknown animation snippet: ${id}`);
  }
  return buildAnimation(file, descriptor, overrides);
};

export const createIdleAnimation = (file: string, overrides?: AnimationOverrides): AnimationSource =>
  useAnimationSnippet('idle.basic', file, overrides);

export const createRunAnimation = (file: string, overrides?: AnimationOverrides): AnimationSource =>
  useAnimationSnippet('run.basic', file, overrides);

export const createJumpAnimation = (file: string, overrides?: AnimationOverrides): AnimationSource =>
  useAnimationSnippet('jump.default', file, overrides);

export const createAttackAnimation = (file: string, overrides?: AnimationOverrides): AnimationSource =>
  useAnimationSnippet('attack.default', file, overrides);